import { Table } from '@chakra-ui/react'
import { Box } from '.'
import { startedOnItsOwnTarget } from '@/utils/rowClick'

/** The props that make a row behave like a button for `onRowClick`. */
function clickableRow (onRowClick, row, index) {
  const open = () => onRowClick(row, index)
  return {
    tabIndex: 0,
    cursor: 'pointer',
    onClick: event => {
      if (startedOnItsOwnTarget(event)) return
      // Finishing a drag-select inside the row is reading, not clicking.
      if (window.getSelection?.()?.toString()) return
      open()
    },
    onKeyDown: event => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      // Only when the row itself has focus: Space on a checkbox in the row is
      // the checkbox's.
      if (event.target !== event.currentTarget) return
      event.preventDefault()
      open()
    },
    _hover: { bg: 'bg.subtle' },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'primary.focusRing',
      outlineOffset: '-2px'
    }
  }
}

export default function SimpleTable ({ headers, rows, variant, striped, size, shadow, rounded, asCard, rowProps, onRowClick }) {
  // `rowProps` wins, so a caller can still override any of this per row.
  const resolveRowProps = (row, index) => {
    const given = typeof rowProps === 'function' ? rowProps(row, index) : rowProps
    if (!onRowClick) return given
    return { ...clickableRow(onRowClick, row, index), ...given }
  }

  const table = (
    <Table.Root
      size={size}
      striped={striped || variant === 'striped'}
    >
      <Table.Header>
        <Table.Row bg='bg.subtle'>
          {headers?.map((header, index) => (
            <Table.ColumnHeader key={`${header}-${index}`} color='fg.muted' fontWeight='medium'>{header}</Table.ColumnHeader>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row, index) => (row &&
          <Table.Row key={index} {...resolveRowProps(row, index)}>
            {row?.map((cell, index) => (
              <Table.Cell key={index}>{cell}</Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )

  if (asCard) {
    return (
      <Box
        overflow='hidden'
        rounded={rounded || 'lg'}
        shadow={shadow || 'md'}
        bg='bg.surface'
        borderWidth='1px'
        borderColor='border.default'
      >
        {table}
      </Box>
    )
  }

  return table
}
